import { useMemo } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { ShortUrlInfo } from '#src/app/shared/hooks/shortUrls';
import { useShortUrlsInfo } from '#src/app/shared/hooks/shortUrls';

import type { ShareChannel } from '../shared/types';

type Options<CH extends ShareChannel = ShareChannel> = {
	disableShortLinks?: boolean;
	getUrl: (channel: CH) => string;
};

export function useShareShortUrls<CH extends ShareChannel = ShareChannel>(
	channels: readonly CH[],
	{ getUrl, disableShortLinks }: Options<CH>,
): Partial<Record<CH, ShortUrlInfo>> {
	const utilityContext = useUtilityContext();
	const { isBot } = utilityContext.userAgent;
	const botsDisabled = isBot();

	// oxlint-disable-next-line typescript/no-unsafe-type-assertion
	const urls = useMemo(
		() =>
			channels.reduce<Record<string, string>>(
				(acc, channel) => ({
					...acc,
					[channel]: utilityContext.resolveUrl(getUrl(channel)),
				}),
				{},
			),
		[channels, getUrl, utilityContext],
	) as Partial<Record<CH, string>>;

	const shouldUseShortLinks = !disableShortLinks && !botsDisabled;
	const shortLinks = useShortUrlsInfo(shouldUseShortLinks ? urls : {}) as Partial<Record<CH, ShortUrlInfo>>;

	return useMemo(() => {
		if (shouldUseShortLinks) return shortLinks;
		// when shortening is disabled (bots, lazy-init panels) we still surface a
		// `ShortUrlInfo` so consumers don't have to special-case the shape; the
		// long url is returned bare (no share_id) to preserve current behavior.
		const result: Partial<Record<CH, ShortUrlInfo>> = {};
		// oxlint-disable-next-line typescript/no-unsafe-type-assertion
		(Object.keys(urls) as CH[]).forEach((channel) => {
			const url = urls[channel];
			if (!url) return;
			result[channel] = {
				url,
				trackingProperties: { generated_share_id: '', generated_share_id_source: 'shortening_disabled' },
			};
		});
		return result;
	}, [shouldUseShortLinks, shortLinks, urls]);
}
